import { jwtDecode } from 'jwt-decode';
import { supportFrameSessionId } from './support-frame';

export const ACCESS_TOKEN_KEY = "ardealy.bo.access.token";
const REFRESH_TOKEN_KEY = "ardealy.bo.refresh.token";

/**
 * A support session's token (Ardealy support working in a vendor's account),
 * kept for the frame it runs in. Session storage is shared by a tab and its
 * frames, so the key names the session: only the frame asks for it, and the
 * super admin's page around it keeps its own sign-in. Short-lived, never refreshed.
 */
export const supportTokenKey = (sessionId: string) => `ardealy.bo.support.token:${sessionId}`;

const frameKey = (): string | null => {
  const id = supportFrameSessionId();
  return id ? supportTokenKey(id) : null;
};

const readSupportToken = (): string | null => {
  const key = frameKey();
  if (!key) return null;
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

class AuthClientStore {
  /** The token every request sends: this tab's support session if there is one, else the sign-in. */
  static getAccessToken() {
    return readSupportToken() ?? localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  static setAccessToken(token: string) {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }

  static removeAccessToken(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    // The preview tag names the user who was signed in; the next one gets their own.
    localStorage.removeItem('ardealy.bo.preview.tag');
  }

  /** A support session has no refresh token: it ends when it ends. */
  static getRefreshToken() {
    if (AuthClientStore.isSupportSession()) return null;
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  static setRefreshToken(token: string) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  static removeRefreshToken(): void {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  // ─── Support sessions ─────────────────────────────────────────────────────

  /** The super admin's own token, whatever this tab is doing (to start a support session). */
  static getSignInAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  static isSupportSession(): boolean {
    return !!readSupportToken();
  }

  /** Only a support frame keeps one. */
  static setSupportToken(token: string): void {
    const key = frameKey();
    if (!key) throw new Error('Not a support session frame');
    sessionStorage.setItem(key, token);
  }

  static removeSupportToken(): void {
    const key = frameKey();
    if (!key) return;
    try {
      sessionStorage.removeItem(key);
    } catch {
      // storage blocked: nothing was kept
    }
  }

  /** The vendor this tab's support session is in, or null outside one. */
  static supportVendorId(): string | null {
    const token = readSupportToken();
    if (!token) return null;
    try {
      return jwtDecode<any>(token).vuuid ?? null;
    } catch {
      return null;
    }
  }

  /** When this tab's support session runs out (ms), or null outside one. */
  static supportExpiresAt(): number | null {
    const token = readSupportToken();
    if (!token) return null;
    try {
      return jwtDecode<any>(token).exp * 1000;
    } catch {
      return 0;
    }
  }
}

export default AuthClientStore;
